import { store } from '../store';
import { updateJobInList, addJob } from '../store/slices/jobsSlice';
import { updateEncoderStatus, updateEncoderStats } from '../store/slices/encodersSlice';
import { setConnectionStatus } from '../store/slices/websocketSlice';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectInterval = null;
    this.reconnectDelay = 3000; // Start with 3 seconds
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.messageHandlers = new Map();
    this.subscriptions = new Set();
    this.isIntentionallyClosed = false;
    this.pendingSubscriptions = []; // Queue subscriptions until connected
    this.isConnected = false;
  }

  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    // Get auth token from Redux store or localStorage
    let token = store.getState().auth.userToken;
    
    // Fallback to localStorage if Redux state not yet hydrated (e.g., after hard refresh)
    if (!token) {
      token = localStorage.getItem('userToken');
    }
    
    if (!token) {
      // This is expected when user is not logged in
      return;
    }

    // Construct WebSocket URL with token
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/ws/user?token=${encodeURIComponent(token)}`;

    this.isIntentionallyClosed = false;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.isConnected = true;
        store.dispatch(setConnectionStatus('connected'));
        this.reconnectDelay = 3000; // Reset delay on successful connection

        // Clear any existing reconnect interval
        if (this.reconnectInterval) {
          clearInterval(this.reconnectInterval);
          this.reconnectInterval = null;
        }

        // Clear pending subscriptions since we'll re-subscribe to everything
        this.pendingSubscriptions = [];

        // Re-subscribe to all tracked subscriptions
        if (this.subscriptions.size > 0) {
          const allSubscriptions = [...this.subscriptions];
          this.send({
            type: 'subscribe',
            events: allSubscriptions
          });
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (_error) {
          /* swallow — non-JSON ws messages ignored */
        }
      };

      this.ws.onclose = (_event) => {
        this.isConnected = false;
        store.dispatch(setConnectionStatus('disconnected'));
        this.ws = null;

        // Only reconnect if not intentionally closed
        if (!this.isIntentionallyClosed) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (_error) => {
        store.dispatch(setConnectionStatus('error'));
      };
    } catch (_error) {
      store.dispatch(setConnectionStatus('error'));
      this.scheduleReconnect();
    }
  }

  disconnect() {
    this.isIntentionallyClosed = true;
    
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
      this.reconnectInterval = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  scheduleReconnect() {
    if (this.reconnectInterval) {
      return; // Already scheduled
    }

    
    this.reconnectInterval = setTimeout(() => {
      this.reconnectInterval = null;
      this.connect();
      
      // Increase delay for next attempt (exponential backoff)
      this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, this.maxReconnectDelay);
    }, this.reconnectDelay);
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      // Check if user is authenticated before warning
      const token = store.getState().auth.userToken || localStorage.getItem('userToken');
      if (token) {
        /* would-warn: ws closed while authenticated */
      }
      // Otherwise, this is expected when not logged in
    }
  }

  subscribe(eventTypes) {
    if (!Array.isArray(eventTypes)) {
      eventTypes = [eventTypes];
    }

    // Check if we're already subscribed to these event types
    const newEventTypes = eventTypes.filter(type => !this.subscriptions.has(type));
    
    if (newEventTypes.length === 0) {
      // Already subscribed to all requested events
      return;
    }

    newEventTypes.forEach(type => this.subscriptions.add(type));

    // If not connected yet, queue the subscription
    if (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.pendingSubscriptions.push(newEventTypes);
      return;
    }

    this.send({
      type: 'subscribe',
      events: newEventTypes
    });
  }

  unsubscribe(eventTypes) {
    if (!Array.isArray(eventTypes)) {
      eventTypes = [eventTypes];
    }

    eventTypes.forEach(type => this.subscriptions.delete(type));

    this.send({
      type: 'unsubscribe',
      events: eventTypes
    });
  }

  handleMessage(data) {
    const { type, event_type, data: eventData } = data;

    // Handle different message types
    switch (type) {
      case 'connection':
        break;

      case 'subscription':
        break;

      case 'ping':
        // Respond to ping
        this.send({ type: 'pong' });
        break;

      case 'event':
        // Handle real-time events
        this.handleEvent(event_type, eventData);
        break;

      default:
    }
  }

  handleEvent(eventType, data) {

    // Dispatch to Redux based on event type
    switch (eventType) {
      // Job events
      case 'job.created':
        store.dispatch(addJob(data));
        break;

      case 'job.updated':
      case 'job.progress':
      case 'job.completed':
      case 'job.failed':
      case 'job.assigned':
        store.dispatch(updateJobInList(data));
        break;

      // Encoder events
      case 'encoder.online':
        store.dispatch(updateEncoderStatus({ 
          id: data.id, 
          is_online: true,
          ...data 
        }));
        // Also dispatch a custom event for components to listen to
        window.dispatchEvent(new CustomEvent('encoder-online', { detail: data }));
        break;

      case 'encoder.offline':
        store.dispatch(updateEncoderStatus({ 
          id: data.id, 
          is_online: false 
        }));
        break;

      case 'encoder.stats':
        store.dispatch(updateEncoderStats(data));
        break;

      case 'encoder.updated':
        // Encoder was successfully updated, update its version in Redux
        store.dispatch(updateEncoderStatus({
          id: data.id,
          is_online: true,
          version: data.version,
          ...data
        }));
        // Also dispatch a custom event that Encoders.jsx can listen to
        window.dispatchEvent(new CustomEvent('encoder-updated', { detail: data }));
        break;

      // File events - these will be handled by components that register handlers
      case 'file.uploaded':
      case 'file.shared':
      case 'file.unshared':
      case 'file.deleted':
      case 'file.renamed': {
        // Trigger any registered handlers for this event
        const handlers = this.messageHandlers.get(eventType) || [];
        handlers.forEach(handler => handler(data));
        break;
      }

      default:
    }
  }

  // Register a handler for a specific event type
  on(eventType, handler) {
    if (!this.messageHandlers.has(eventType)) {
      this.messageHandlers.set(eventType, []);
    }
    this.messageHandlers.get(eventType).push(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.messageHandlers.get(eventType) || [];
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    };
  }

  // Remove all handlers for an event type
  off(eventType) {
    this.messageHandlers.delete(eventType);
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;